export const formatShortDateTime = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const FIELD_LABELS = {
  type: 'Type',
  first_name: 'First name',
  last_name: 'Last name',
  company_name: 'Company name',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  address_2: 'Address line 2',
  city: 'City',
  state: 'State',
  zip: 'Zip',
  latitude: 'Latitude',
  longitude: 'Longitude',
  travel_distance: 'Travel distance',
  ssn: 'SSN',
  ein: 'EIN',
  blacklisted: 'Blacklisted',
  blocked_reason: 'Blocked reason',
  is_active: 'Active',
  category: 'Category',
  file_name: 'File name',
};

const formatBoolean = (value) => (
  value === true || value === 'true' ? 'Yes' : value === false || value === 'false' ? 'No' : String(value)
);

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (field === 'blacklisted' || field === 'is_active') return formatBoolean(value);
  if (typeof value === 'boolean') return formatBoolean(value);
  return String(value);
};

const formatChanges = (changes = []) => {
  if (!Array.isArray(changes) || changes.length === 0) return null;
  return changes
    .map((change) => {
      const field = FIELD_LABELS[change.field] || change.field;
      const fromValue = formatFieldValue(change.field, change.from);
      const toValue = formatFieldValue(change.field, change.to);
      return `${field}: ${fromValue} -> ${toValue}`;
    })
    .join('\n');
};

const entityLabel = (metadata = {}) => (metadata.type === 'vendor' ? 'Vendor' : 'Technician');

const actionMessage = (action, metadata = {}) => {
  const label = entityLabel(metadata);
  switch (action) {
    case 'technician.created':
      return `${label} created`;
    case 'technician.updated':
      return `${label} updated`;
    case 'technician.blacklisted_updated':
      return 'Blacklist status updated';
    case 'technician.active_updated':
      return 'Active status updated';
    case 'technician.attachment_uploaded':
      return 'Document uploaded';
    case 'technician.attachment_deleted':
      return 'Document deleted';
    default:
      return action || 'Activity';
  }
};

const displayNameFromMetadata = (metadata = {}) => {
  if (metadata.type === 'vendor' && metadata.company_name) return metadata.company_name;
  const name = [metadata.first_name, metadata.last_name].filter(Boolean).join(' ').trim();
  return name || metadata.company_name || null;
};

export const formatTechnicianLogEntry = (log) => {
  if (!log) return '';
  const base = actionMessage(log.action, log.metadata);
  const changesText = formatChanges(log.metadata?.changes);
  if (changesText) return `${base}\n${changesText}`;

  if (
    log.action === 'technician.attachment_uploaded'
    || log.action === 'technician.attachment_deleted'
  ) {
    const parts = [];
    if (log.metadata?.category) parts.push(`Category: ${log.metadata.category}`);
    if (log.metadata?.file_name) parts.push(`File: ${log.metadata.file_name}`);
    if (parts.length) return `${base}\n${parts.join('\n')}`;
    return base;
  }

  if (log.action === 'technician.created') {
    const parts = [];
    const displayName = displayNameFromMetadata(log.metadata);
    if (displayName) parts.push(`Name: ${displayName}`);
    if (log.metadata?.type) parts.push(`Type: ${log.metadata.type}`);
    if (log.metadata?.phone) parts.push(`Phone: ${log.metadata.phone}`);
    if (log.metadata?.email) parts.push(`Email: ${log.metadata.email}`);
    if (log.metadata?.ssn) parts.push(`SSN: ${log.metadata.ssn}`);
    if (log.metadata?.ein) parts.push(`EIN: ${log.metadata.ein}`);
    if (parts.length) return `${base}\n${parts.join('\n')}`;
  }

  return base;
};
